import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useProperties } from '@/hooks/useProperties';
import { useCreateTask } from '@/hooks/admin/AdminTodosTabs/useTasks';
import {
  useCreateTaskTag,
  useTaskTags,
} from '@/hooks/admin/AdminTodosTabs/useTasks';
import { useUsers } from '@/hooks/useUsers';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import CustomDatePicker from '@/components/CustomDatePicker';
import { format } from 'date-fns';
import { translateRoleToSpanish } from '@/utils/shared/translateRoleToSpanish';
import { useAuth } from '@/contexts/AuthContext';
import { Role } from '@/constants/rolePermissions';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

interface TaskFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDueDate?: Date | null;
  onSubmit?: () => void;
  initialAssignedUserId?: string | null;
}

interface NewTagForm {
  name: string;
  color: string;
}

const defaultColors = [
  '#10B981',
  '#3B82F6',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#EC4899',
  '#6366F1',
];

const taskSchema = z.object({
  title: z.string().min(1, 'El título es obligatorio'),
  description: z.string().optional(),
  due_date: z.string().optional(),
  assigned_user_id: z.string().optional(),
  property_id: z.string().optional(),
  tag_id: z.string().optional(),
});

type TaskFormData = z.infer<typeof taskSchema>;

const TaskFormModal = ({
  open,
  onOpenChange,
  defaultDueDate,
  onSubmit,
  initialAssignedUserId = null,
}: TaskFormModalProps) => {
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [newTag, setNewTag] = useState<NewTagForm>({
    name: '',
    color: defaultColors[0],
  });
  const [propertyOpen, setPropertyOpen] = useState(false);

  const createTask = useCreateTask();
  const createTag = useCreateTaskTag();

  const { data: tags = [], error: tagsError } = useTaskTags();
  const { data: properties = [], error: propertiesError } = useProperties();

  const { profile } = useAuth();

  const form = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      title: '',
      description: '',
      due_date: defaultDueDate ? format(defaultDueDate, 'yyyy-MM-dd') : '',
      assigned_user_id: initialAssignedUserId || '__none__',
      property_id: '__none__',
      tag_id: '__none__',
    },
  });

  const propertyId = form.watch('property_id');
  const tagId = form.watch('tag_id');

  const userRoles = useMemo<Role[]>(() => {
    if (!profile) return [];

    switch (profile.role) {
      case 'super_admin':
        return ['cleaning', 'property_manager'];
      case 'property_manager':
      case 'cleaning':
        return ['cleaning'];
      default:
        return [];
    }
  }, [profile]);

  const { data: users = [], error: usersError } = useUsers(userRoles);

  const availableUsers = useMemo(() => {
    return users.filter((u) => userRoles.includes(u.role));
  }, [users, userRoles]);

  useEffect(() => {
    if (open) {
      form.reset({
        title: '',
        description: '',
        due_date: defaultDueDate ? format(defaultDueDate, 'yyyy-MM-dd') : '',
        assigned_user_id: initialAssignedUserId || '__none__',
        property_id: '__none__',
        tag_id: '__none__',
      });
      setShowNewTagForm(false);
      setNewTag({ name: '', color: defaultColors[0] });
    }
  }, [open, defaultDueDate, initialAssignedUserId, form]);

  const handleSubmit = (data: TaskFormData) => {
    const taskData = {
      title: data.title.trim(),
      description: data.description?.trim() || undefined,
      due_date: data.due_date || undefined,
      assigned_user_id:
        data.assigned_user_id === '__none__' ? undefined : data.assigned_user_id,
      property_id: data.property_id === '__none__' ? undefined : data.property_id,
      tag_id: data.tag_id === '__none__' ? undefined : data.tag_id,
      status: 'pending' as const,
      sort_order: 0,
      created_by_id: '', // Will be handled by the hook
    };

    createTask.mutate(taskData, {
      onSuccess: (result) => {
        console.log('TaskFormModal: Tarea creada exitosamente', result);
        onOpenChange(false);
        if (onSubmit) {
          onSubmit();
        }
      },
      onError: (error) => {
        console.error('TaskFormModal: Error al crear tarea', error);
      },
    });
  };

  const handleCreateTag = async () => {
    if (!newTag.name.trim()) return;

    createTag.mutate(newTag, {
      onSuccess: (createdTag) => {
        form.setValue('tag_id', createdTag.id);
        setShowNewTagForm(false);
        setNewTag({ name: '', color: defaultColors[0] });
      },
      onError: (error) => {
        console.error('Error al crear la etiqueta', error);
      },
    });
  };

  if (tagsError || usersError || propertiesError) {
    // Ideally use a better error display
    console.error('Hubo un problema al cargar los datos necesarios.');
  }

  return (
    <FormModal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title="Nueva Tarea"
      form={form}
      onSubmit={handleSubmit}
      submitLabel={createTask.isPending ? 'Creando...' : 'Crear Tarea'}
      isLoading={createTask.isPending}
      maxWidth="max-w-[500px]"
    >
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Título *</FormLabel>
              <FormControl>
                <Input placeholder="Título de la tarea" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descripción</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Descripción de la tarea (opcional)"
                  rows={3}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="due_date"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Fecha de vencimiento</FormLabel>
              <CustomDatePicker
                selected={field.value ? new Date(field.value + 'T12:00:00') : undefined}
                onSelect={(date) =>
                  field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                }
                placeholder="Selecciona una fecha"
                isAdminContext={true}
                className="flex-1 h-10 w-full"
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="assigned_user_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Usuario asignado</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar usuario (opcional)" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {availableUsers.map((user) => (
                    <SelectItem
                      key={user.user_id}
                      value={user.user_id || `user-${user.id}`}
                    >
                      {user.first_name} {user.last_name}
                      {' - '}
                      <span className="text-muted-foreground text-xs">
                        {translateRoleToSpanish(user.role)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="property_id"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Propiedad</FormLabel>
              <Popover open={propertyOpen} onOpenChange={setPropertyOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={propertyOpen}
                      className="w-full justify-between"
                    >
                      {field.value !== '__none__' && field.value !== '__external__'
                        ? properties.find((property) => property.id === field.value)
                          ?.shortName || 'Seleccionar propiedad...'
                        : field.value === '__external__'
                          ? 'Propiedad externa'
                          : 'Seleccionar propiedad (opcional)'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>

                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Buscar propiedad..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron propiedades.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__none__"
                          onSelect={() => {
                            form.setValue('property_id', '__none__');
                            setPropertyOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              field.value === '__none__'
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                          Sin propiedad asignada
                        </CommandItem>

                        <CommandItem
                          value="__external__"
                          onSelect={() => {
                            form.setValue('property_id', '__external__');
                            setPropertyOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              field.value === '__external__'
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                          Propiedad externa
                        </CommandItem>

                        {properties.map((property) => (
                          <CommandItem
                            key={property.id}
                            value={property.shortName || property.title}
                            onSelect={() => {
                              form.setValue('property_id', property.id);
                              setPropertyOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                field.value === property.id
                                  ? 'opacity-100'
                                  : 'opacity-0',
                              )}
                            />
                            {property.shortName || property.title}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label htmlFor="tag">Etiqueta</Label>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowNewTagForm(true)}
              className="text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Nueva etiqueta
            </Button>
          </div>

          <FormField
            control={form.control}
            name="tag_id"
            render={({ field }) => (
              <FormItem>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar etiqueta (opcional)" />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    <SelectItem value="__none__">Sin etiqueta</SelectItem>
                    {tags
                      .filter((tag) => tag.id)
                      .map((tag) => (
                        <SelectItem key={tag.id} value={tag.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {showNewTagForm && (
            <Card className="mt-3">
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="newTagName">Nombre de la etiqueta</Label>
                    <Input
                      id="newTagName"
                      value={newTag.name}
                      onChange={(e) =>
                        setNewTag((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder="Nombre de la etiqueta"
                    />
                  </div>

                  <div>
                    <Label>Color</Label>
                    <div className="flex gap-2 mt-2">
                      {defaultColors.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`w-6 h-6 rounded-full border-2 ${newTag.color === color
                              ? 'border-gray-900'
                              : 'border-gray-300'
                            }`}
                          style={{ backgroundColor: color }}
                          onClick={() =>
                            setNewTag((prev) => ({ ...prev, color }))
                          }
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateTag}
                      disabled={createTag.isPending || !newTag.name.trim()}
                    >
                      Crear etiqueta
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowNewTagForm(false)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </FormModal>
  );
};

export default TaskFormModal;
